import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Users, Key, LogOut, Shield, Trash2, User, Mail, Search, ChevronDown, ChevronRight, FlaskConical, UserPlus, Plus, TrendingUp, Flame, Clock, Dumbbell, Mic, PenLine, Upload, FileSpreadsheet, CheckCircle, XCircle, Headphones, Activity, BarChart3, Calendar, Eye, Radio, Wind, BellRing, GraduationCap } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import { getBaseUrl } from '@/utils/api';
import { getToken } from '@/utils/localStorage';

const API_URL = getBaseUrl();

// Helper to identify test accounts
const isTestAccount = (email) => {
  if (!email) return false;
  const testPatterns = [
    'test',
    'demo',
    'example.com',
    'test.com',
    'fake',
    'sample',
    '@mailinator',
    '@tempmail',
    '@guerrillamail'
  ];
  const lowerEmail = email.toLowerCase();
  return testPatterns.some(pattern => lowerEmail.includes(pattern));
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const [coaches, setCoaches] = useState([]);
  const [clients, setClients] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('coaches');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCoach, setExpandedCoach] = useState(null);
  const [coachClients, setCoachClients] = useState({});
  const [clientTab, setClientTab] = useState('clients'); // 'clients' or 'test'
  const [newWhitelistEmail, setNewWhitelistEmail] = useState('');
  const [newWhitelistName, setNewWhitelistName] = useState('');
  const [newWhitelistNote, setNewWhitelistNote] = useState('');
  const [addingToWhitelist, setAddingToWhitelist] = useState(false);
  const [userProgress, setUserProgress] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [detailedUserData, setDetailedUserData] = useState([]);
  const [loadingDetailed, setLoadingDetailed] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkImportResults, setBulkImportResults] = useState(null);
  const [olgOverview, setOlgOverview] = useState({ sessions: [], rooms: [] });
  const [loadingOlg, setLoadingOlg] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!user || user.role !== 'master_admin') {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [coachesRes, clientsRes, whitelistRes] = await Promise.all([
        fetch(`${API_URL}/api/coaches`, { credentials: 'include' }),
        fetch(`${API_URL}/api/clients`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/whitelist`, { credentials: 'include' })
      ]);
      
      if (coachesRes.ok) setCoaches(await coachesRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (whitelistRes.ok) setWhitelist(await whitelistRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoachClients = async (coachId) => {
    if (coachClients[coachId]) return;
    try {
      const response = await fetch(`${API_URL}/api/coaches/${coachId}/clients`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCoachClients(prev => ({ ...prev, [coachId]: data }));
      }
    } catch (error) {
      console.error('Error fetching coach clients:', error);
    }
  };

  const fetchUserProgress = async () => {
    setLoadingProgress(true);
    try {
      const token = getToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/admin/users/progress`, {
        headers,
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUserProgress(data);
      }
    } catch (error) {
      console.error('Error fetching user progress:', error);
    } finally {
      setLoadingProgress(false);
    }
  };

  const fetchDetailedUserData = async () => {
    setLoadingDetailed(true);
    try {
      const token = getToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/admin/users/detailed`, {
        headers,
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDetailedUserData(data);
      }
    } catch (error) {
      console.error('Error fetching detailed user data:', error);
    } finally {
      setLoadingDetailed(false);
    }
  };

  const fetchOlgOverview = async () => {
    setLoadingOlg(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API_URL}/api/olg/admin/overview`, {
        headers,
        credentials: 'include',
      });
      if (response.ok) {
        setOlgOverview(await response.json());
      }
    } catch (error) {
      console.error('Error fetching OLG overview:', error);
    } finally {
      setLoadingOlg(false);
    }
  };

  const endOlgSession = async (code) => {
    if (!window.confirm(`End session ${code}? All rooms in this session will be deleted.`)) return;
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await fetch(`${API_URL}/api/olg/sessions/${code}/end`, { method: 'POST', headers, credentials: 'include' });
      fetchOlgOverview();
    } catch (e) { console.error(e); }
  };

  const endOlgRoom = async (code) => {
    if (!window.confirm(`Delete room ${code}?`)) return;
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Demo rooms need the demo endpoint (guards demo-only)
      const room = (olgOverview.rooms || []).find((r) => r.code === code);
      const url = room?.demo
        ? `${API_URL}/api/olg/admin/demo-room/${code}/end`
        : `${API_URL}/api/olg/rooms/${code}/end`;
      await fetch(url, { method: 'POST', headers, credentials: 'include' });
      fetchOlgOverview();
    } catch (e) { console.error(e); }
  };

  // Whitelist management functions
  const handleAddToWhitelist = async (e) => {
    e.preventDefault();
    if (!newWhitelistEmail.trim()) return;
    
    setAddingToWhitelist(true);
    try {
      const token = getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/admin/whitelist`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ 
          email: newWhitelistEmail.trim().toLowerCase(),
          name: newWhitelistName.trim() || null,
          note: newWhitelistNote.trim() || null
        })
      });
      
      if (response.ok) {
        const newEntry = await response.json();
        setWhitelist(prev => [...prev, newEntry]);
        setNewWhitelistEmail('');
        setNewWhitelistName('');
        setNewWhitelistNote('');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add email to whitelist');
      }
    } catch (error) {
      console.error('Error adding to whitelist:', error);
      alert('Failed to add email to whitelist');
    } finally {
      setAddingToWhitelist(false);
    }
  };

  // Bulk import from CSV
  const handleBulkImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setBulkImporting(true);
    setBulkImportResults(null);
    
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      // Skip header row if present
      const startIndex = lines[0].toLowerCase().includes('name') || lines[0].toLowerCase().includes('email') ? 1 : 0;
      
      const results = { success: [], failed: [], skipped: [] };
      const token = getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        // Parse CSV line (handle commas in quoted fields)
        const parts = line.split(',').map(p => p.trim().replace(/^["']|["']$/g, ''));
        const name = parts[0] || '';
        const email = parts[1] || parts[0]; // If only one column, treat as email
        
        if (!email || !email.includes('@')) {
          results.skipped.push({ line: i + 1, reason: 'Invalid email', data: line });
          continue;
        }
        
        // Check if already in whitelist
        if (whitelist.some(w => w.email.toLowerCase() === email.toLowerCase())) {
          results.skipped.push({ line: i + 1, reason: 'Already whitelisted', data: email });
          continue;
        }
        
        try {
          const response = await fetch(`${API_URL}/api/admin/whitelist`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({ 
              email: email.toLowerCase(),
              name: name || null,
              note: 'Bulk import'
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            results.success.push({ email, name });
            setWhitelist(prev => [...prev, data]);
          } else {
            results.failed.push({ email, name, reason: 'API error' });
          }
        } catch (err) {
          results.failed.push({ email, name, reason: err.message });
        }
      }
      
      setBulkImportResults(results);
    } catch (error) {
      console.error('Error parsing CSV:', error);
      alert('Failed to parse CSV file. Please ensure it\'s a valid CSV format.');
    } finally {
      setBulkImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveFromWhitelist = async (entryId, email) => {
    if (!window.confirm(`Remove ${email} from whitelist?`)) return;
    
    try {
      const token = getToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/admin/whitelist/${entryId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });
      
      if (response.ok) {
        setWhitelist(prev => prev.filter(e => e.id !== entryId));
      } else {
        alert('Failed to remove from whitelist');
      }
    } catch (error) {
      console.error('Error removing from whitelist:', error);
    }
  };

  const handleDeleteCoach = async (coachId, coachName) => {
    if (!window.confirm(`Are you sure you want to delete coach ${coachName}? This will unlink all their clients.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/coaches/${coachId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setCoaches(coaches.filter(c => c.id !== coachId));
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to delete coach');
      }
    } catch (error) {
      console.error('Error deleting coach:', error);
    }
  };

  const handleDeleteClient = async (clientId, clientName) => {
    if (!window.confirm(`Are you sure you want to delete client "${clientName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/clients/${clientId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setClients(clients.filter(c => c.id !== clientId));
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to delete client');
      }
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const handleDeleteAllClients = async () => {
    const count = clients.length;
    if (!window.confirm(`Are you sure you want to delete ALL ${count} clients? This action cannot be undone.`)) {
      return;
    }
    
    // Double confirmation for destructive action
    if (!window.confirm(`FINAL WARNING: This will permanently delete ${count} client accounts. Type OK in the next prompt to confirm.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/clients/all`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Successfully deleted ${data.deleted_count} clients`);
        setClients([]);
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to delete clients');
      }
    } catch (error) {
      console.error('Error deleting all clients:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      // Ignore logout errors
    }
    logout();
    navigate('/login');
  };

  const toggleCoachExpand = (coachId) => {
    if (expandedCoach === coachId) {
      setExpandedCoach(null);
    } else {
      setExpandedCoach(coachId);
      fetchCoachClients(coachId);
    }
  };

  const filteredCoaches = coaches.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.coach_id_key.includes(searchTerm)
  );

  // Separate real clients from test accounts
  const realClients = clients.filter(c => !isTestAccount(c.email));
  const testClients = clients.filter(c => isTestAccount(c.email));

  const filteredRealClients = realClients.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTestClients = testClients.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredWhitelist = whitelist.filter(w =>
    w.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (w.note && w.note.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory-mist flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-burnt-terra"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory-mist pb-16">
      {/* Header */}
      <header className="bg-obsidian-night text-white px-4 sm:px-6 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center min-w-0">
            <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-burnt-terra mr-2 sm:mr-3 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="font-display text-lg sm:text-2xl font-bold truncate">Master Admin</h1>
              <p className="text-ivory-mist/60 font-body text-xs sm:text-sm truncate">{user?.name} (0001)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/teachers-corner')}
              className="border-burnt-terra/40 text-burnt-terra hover:bg-burnt-terra/10 px-2 sm:px-4"
              data-testid="admin-teachers-corner-btn"
            >
              <GraduationCap className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Teacher's Corner</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/operation-listen')}
              className="border-burnt-terra/40 text-burnt-terra hover:bg-burnt-terra/10 px-2 sm:px-4"
              data-testid="admin-olg-master-btn"
            >
              <Radio className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">OL Master View</span>
            </Button>
            <Button variant="outline" onClick={handleLogout} className="border-ivory-mist/30 text-ivory-mist hover:bg-ivory-mist/10 px-2 sm:px-4">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card className="bg-white">
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-center text-center sm:text-left">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-burnt-terra/10 rounded-full flex items-center justify-center mb-2 sm:mb-0">
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 text-burnt-terra" />
                </div>
                <div className="sm:ml-4">
                  <p className="text-xs sm:text-sm text-shadow-pine/60 font-body">Coaches</p>
                  <p className="text-xl sm:text-2xl font-display font-bold text-obsidian-night">{coaches.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-center text-center sm:text-left">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-shadow-pine/10 rounded-full flex items-center justify-center mb-2 sm:mb-0">
                  <User className="w-5 h-5 sm:w-6 sm:h-6 text-shadow-pine" />
                </div>
                <div className="sm:ml-4">
                  <p className="text-xs sm:text-sm text-shadow-pine/60 font-body">Clients</p>
                  <p className="text-xl sm:text-2xl font-display font-bold text-obsidian-night">{clients.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-center text-center sm:text-left">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-shadow-pine/20 rounded-full flex items-center justify-center mb-2 sm:mb-0">
                  <Key className="w-5 h-5 sm:w-6 sm:h-6 text-shadow-pine" />
                </div>
                <div className="sm:ml-4">
                  <p className="text-xs sm:text-sm text-shadow-pine/60 font-body">Master Key</p>
                  <p className="text-xl sm:text-2xl font-display font-bold text-obsidian-night">0001</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative w-full md:max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
            <Input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-shadow-pine/20"
            />
          </div>
        </div>

        {/* Tabs - horizontally scrollable on mobile */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
          <Button
            variant={activeTab === 'coaches' ? 'default' : 'outline'}
            onClick={() => setActiveTab('coaches')}
            className={`whitespace-nowrap flex-shrink-0 ${activeTab === 'coaches' ? 'bg-burnt-terra text-white' : 'border-shadow-pine/30'}`}
          >
            <Users className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Coaches</span>
            <span className="sm:hidden">Coach</span>
            <span className="ml-1">({coaches.length})</span>
          </Button>
          <Button
            variant={activeTab === 'clients' ? 'default' : 'outline'}
            onClick={() => setActiveTab('clients')}
            className={`whitespace-nowrap flex-shrink-0 ${activeTab === 'clients' ? 'bg-burnt-terra text-white' : 'border-shadow-pine/30'}`}
          >
            <User className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Clients</span>
            <span className="sm:hidden">Client</span>
            <span className="ml-1">({realClients.length})</span>
          </Button>
          <Button
            variant={activeTab === 'progress' ? 'default' : 'outline'}
            onClick={() => {
              setActiveTab('progress');
              if (userProgress.length === 0) fetchUserProgress();
            }}
            className={`whitespace-nowrap flex-shrink-0 ${activeTab === 'progress' ? 'bg-burnt-terra text-white' : 'border-shadow-pine/30'}`}
          >
            <TrendingUp className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">User Progress</span>
            <span className="sm:hidden">Progress</span>
          </Button>
          <Button
            variant={activeTab === 'insights' ? 'default' : 'outline'}
            onClick={() => {
              setActiveTab('insights');
              if (detailedUserData.length === 0) fetchDetailedUserData();
            }}
            className={`whitespace-nowrap flex-shrink-0 ${activeTab === 'insights' ? 'bg-burnt-terra text-white' : 'border-shadow-pine/30'}`}
          >
            <BarChart3 className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">User Insights</span>
            <span className="sm:hidden">Insights</span>
          </Button>
          <Button
            variant={activeTab === 'olg' ? 'default' : 'outline'}
            onClick={() => {
              setActiveTab('olg');
              fetchOlgOverview();
            }}
            className={`whitespace-nowrap flex-shrink-0 ${activeTab === 'olg' ? 'bg-burnt-terra text-white' : 'border-burnt-terra/40 text-burnt-terra'}`}
            data-testid="admin-tab-olg"
          >
            <Radio className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Operation Listen</span>
            <span className="sm:hidden">OL Game</span>
          </Button>
          <Button
            variant={activeTab === 'test' ? 'default' : 'outline'}
            onClick={() => setActiveTab('test')}
            className={`whitespace-nowrap flex-shrink-0 ${activeTab === 'test' ? 'bg-burnt-terra text-white' : 'border-shadow-pine/30'}`}
          >
            <FlaskConical className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Test Accounts</span>
            <span className="sm:hidden">Test</span>
            <span className="ml-1">({testClients.length})</span>
          </Button>
          <Button
            variant={activeTab === 'whitelist' ? 'default' : 'outline'}
            onClick={() => setActiveTab('whitelist')}
            className={`whitespace-nowrap flex-shrink-0 ${activeTab === 'whitelist' ? 'bg-shadow-pine text-white' : 'border-shadow-pine/30'}`}
          >
            <UserPlus className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Whitelist</span>
            <span className="sm:hidden">Access</span>
            <span className="ml-1">({whitelist.length})</span>
          </Button>
          <Button
            variant={activeTab === 'app' ? 'default' : 'outline'}
            onClick={() => setActiveTab('app')}
            className={`whitespace-nowrap flex-shrink-0 ${activeTab === 'app' ? 'bg-shadow-pine text-white' : 'border-shadow-pine/30'}`}
          >
            <Mic className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">User Experience</span>
            <span className="sm:hidden">App</span>
          </Button>
        </div>

        {/* Content */}
        {activeTab === 'coaches' ? (
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="font-display text-obsidian-night">All Coaches</CardTitle>
              <CardDescription className="font-body text-shadow-pine/60">
                Manage coaches and view their clients
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-shadow-pine/10">
                {filteredCoaches.map((coach) => (
                  <div key={coach.id}>
                    <div
                      className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between hover:bg-ivory-mist/50 -mx-4 px-4 cursor-pointer transition-colors gap-3"
                      onClick={() => toggleCoachExpand(coach.id)}
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-burnt-terra/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Users className="w-5 h-5 text-burnt-terra" />
                        </div>
                        <div className="ml-3 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-body font-semibold text-obsidian-night truncate">{coach.name}</p>
                            {coach.coach_id_key === '0001' && (
                              <span className="text-xs bg-obsidian-night text-white px-2 py-0.5 rounded flex-shrink-0">Master</span>
                            )}
                          </div>
                          <p className="text-sm text-shadow-pine/60 font-body flex items-center truncate">
                            <Mail className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{coach.email}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4 ml-13 sm:ml-0">
                        <div className="text-center min-w-[50px]">
                          <p className="font-mono font-bold text-burnt-terra text-sm sm:text-base">{coach.coach_id_key}</p>
                          <p className="text-xs text-shadow-pine/60 font-body">ID Key</p>
                        </div>
                        <div className="text-center min-w-[50px]">
                          <p className="font-bold text-obsidian-night text-sm sm:text-base">{coach.client_count}</p>
                          <p className="text-xs text-shadow-pine/60 font-body">Clients</p>
                        </div>
                        {coach.coach_id_key !== '0001' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCoach(coach.id, coach.name);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        {expandedCoach === coach.id ? (
                          <ChevronDown className="w-5 h-5 text-shadow-pine/40 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-shadow-pine/40 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                    {expandedCoach === coach.id && coachClients[coach.id] && (
                      <div className="bg-ivory-mist/50 px-4 py-3 -mx-4 mb-2">
                        <p className="text-sm font-semibold text-shadow-pine mb-2">Clients:</p>
                        {coachClients[coach.id].length === 0 ? (
                          <p className="text-sm text-shadow-pine/60">No clients yet</p>
                        ) : (
                          <div className="space-y-2">
                            {coachClients[coach.id].map(client => (
                              <div key={client.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white p-2 rounded gap-1">
                                <div className="min-w-0">
                                  <p className="font-body text-sm font-medium truncate">{client.name}</p>
                                  <p className="text-xs text-shadow-pine/60 truncate">{client.email}</p>
                                </div>
                                <div className="text-left sm:text-right flex sm:flex-col gap-2 sm:gap-0">
                                  <p className="text-sm font-bold text-burnt-terra">{client.streak || 0} day streak</p>
                                  <p className="text-xs text-shadow-pine/60">{client.workout_count || 0} workouts</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : activeTab === 'clients' ? (
          <Card className="bg-white">
            <CardHeader className="px-4 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <CardTitle className="font-display text-obsidian-night text-base sm:text-lg">Clients</CardTitle>
                  <CardDescription className="font-body text-shadow-pine/60 text-xs sm:text-sm">
                    Manage registered clients
                  </CardDescription>
                </div>
                {filteredRealClients.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteAllClients}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 text-xs sm:text-sm self-start sm:self-auto"
                  >
                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    Delete All
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              {filteredRealClients.length === 0 ? (
                <div className="text-center py-8 text-shadow-pine/60">
                  <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No clients found</p>
                </div>
              ) : (
                <div className="divide-y divide-shadow-pine/10">
                  {filteredRealClients.map((client) => (
                    <div key={client.id} className="py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex items-center min-w-0">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-shadow-pine/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 sm:w-5 sm:h-5 text-shadow-pine" />
                        </div>
                        <div className="ml-2 sm:ml-3 min-w-0">
                          <p className="font-body font-semibold text-obsidian-night text-sm sm:text-base truncate">{client.name}</p>
                          <p className="text-xs sm:text-sm text-shadow-pine/60 font-body flex items-center">
                            <Mail className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{client.email}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 ml-10 sm:ml-0">
                        <div className="text-left sm:text-right">
                          <p className="font-bold text-obsidian-night text-sm">{client.workout_count || 0}</p>
                          <p className="text-xs text-shadow-pine/60 font-body">Workouts</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClient(client.id, client.name)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : activeTab === 'test' && (
          <Card className="bg-white border-burnt-terra/30">
            <CardHeader className="border-b border-burnt-terra/20 px-4 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-burnt-terra" />
                  <CardTitle className="font-display text-obsidian-night text-base sm:text-lg">Test Accounts</CardTitle>
                </div>
                {filteredTestClients.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteAllClients}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 text-xs sm:text-sm"
                  >
                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    Delete All
                  </Button>
                )}
              </div>
              <CardDescription className="font-body text-shadow-pine/60">
                Accounts with test/demo/example emails for testing purposes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredTestClients.length === 0 ? (
                <div className="text-center py-8 text-shadow-pine/60">
                  <FlaskConical className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No test accounts found</p>
                </div>
              ) : (
                <div className="divide-y divide-shadow-pine/10">
                  {filteredTestClients.map((client) => (
                    <div key={client.id} className="py-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-burnt-terra/20 rounded-full flex items-center justify-center">
                          <FlaskConical className="w-5 h-5 text-burnt-terra" />
                        </div>
                        <div className="ml-3">
                          <div className="flex items-center gap-2">
                            <p className="font-body font-semibold text-obsidian-night">{client.name}</p>
                            <span className="text-xs bg-burnt-terra/20 text-burnt-terra px-2 py-0.5 rounded">Test</span>
                          </div>
                          <p className="text-sm text-shadow-pine/60 font-body flex items-center">
                            <Mail className="w-3 h-3 mr-1" />
                            {client.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-obsidian-night">{client.workout_count || 0}</p>
                          <p className="text-xs text-shadow-pine/60 font-body">Workouts</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClient(client.id, client.name)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* User Progress Tab */}
        {activeTab === 'progress' && (
          <Card className="bg-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-display text-obsidian-night flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-burnt-terra" />
                    User Progress
                  </CardTitle>
                  <CardDescription className="font-body text-shadow-pine/60">
                    View all users&apos; activity across The Body, The Voice, and The Story pillars
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchUserProgress}
                  disabled={loadingProgress}
                >
                  {loadingProgress ? 'Loading...' : 'Refresh'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingProgress ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burnt-terra"></div>
                </div>
              ) : userProgress.length === 0 ? (
                <div className="text-center py-8 text-shadow-pine/60">
                  <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No user progress data yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 p-4 bg-burnt-terra/10 rounded-lg">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-burnt-terra">
                        {userProgress.reduce((sum, u) => sum + u.total_sessions, 0)}
                      </p>
                      <p className="text-xs text-burnt-terra">Total Sessions</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-burnt-terra">
                        {userProgress.reduce((sum, u) => sum + u.physical_sessions, 0)}
                      </p>
                      <p className="text-xs text-burnt-terra">The Body</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-shadow-pine">
                        {userProgress.reduce((sum, u) => sum + u.voice_sessions, 0)}
                      </p>
                      <p className="text-xs text-shadow-pine">The Voice</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-shadow-pine">
                        {userProgress.reduce((sum, u) => sum + u.writing_sessions, 0)}
                      </p>
                      <p className="text-xs text-shadow-pine">The Story</p>
                    </div>
                  </div>

                  {/* User List */}
                  <div className="divide-y divide-shadow-pine/10">
                    {userProgress.map((userProg) => (
                      <div key={userProg.user_id} className="py-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          {/* User Info */}
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-burnt-terra/20 rounded-full flex items-center justify-center flex-shrink-0">
                              <User className="w-5 h-5 text-burnt-terra" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-obsidian-night truncate">{userProg.name}</p>
                              <p className="text-xs text-shadow-pine/60 truncate">{userProg.email}</p>
                            </div>
                          </div>
                          
                          {/* Streak Badge */}
                          <div className="flex items-center gap-2 ml-13 sm:ml-0">
                            {userProg.current_streak > 0 && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-burnt-terra/20 rounded-full">
                                <Flame className="w-4 h-4 text-burnt-terra" />
                                <span className="text-sm font-bold text-burnt-terra">{userProg.current_streak}</span>
                                <span className="text-xs text-burnt-terra">day streak</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Progress Stats */}
                        <div className="mt-3 ml-13 grid grid-cols-4 gap-2">
                          <div className="text-center p-2 bg-ivory-mist rounded-lg">
                            <p className="text-lg font-bold text-obsidian-night">{userProg.total_sessions}</p>
                            <p className="text-xs text-shadow-pine/60">Total</p>
                          </div>
                          <div className="text-center p-2 bg-burnt-terra/10 rounded-lg">
                            <div className="flex items-center justify-center gap-1">
                              <Dumbbell className="w-3 h-3 text-burnt-terra" />
                              <p className="text-lg font-bold text-burnt-terra">{userProg.physical_sessions}</p>
                            </div>
                            <p className="text-xs text-burnt-terra">The Body</p>
                          </div>
                          <div className="text-center p-2 bg-ivory-mist rounded-lg">
                            <div className="flex items-center justify-center gap-1">
                              <Mic className="w-3 h-3 text-shadow-pine" />
                              <p className="text-lg font-bold text-shadow-pine">{userProg.voice_sessions}</p>
                            </div>
                            <p className="text-xs text-shadow-pine">The Voice</p>
                          </div>
                          <div className="text-center p-2 bg-shadow-pine/10 rounded-lg">
                            <div className="flex items-center justify-center gap-1">
                              <PenLine className="w-3 h-3 text-shadow-pine" />
                              <p className="text-lg font-bold text-shadow-pine">{userProg.writing_sessions}</p>
                            </div>
                            <p className="text-xs text-shadow-pine">The Story</p>
                          </div>
                        </div>
                        
                        {/* Last Activity */}
                        {userProg.last_session_date && (
                          <div className="mt-2 ml-13 flex items-center gap-1 text-xs text-shadow-pine/50">
                            <Clock className="w-3 h-3" />
                            <span>Last active: {new Date(userProg.last_session_date).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* User Insights Tab - Detailed Streaks, Voice Analysis, Practice Progress */}
        {activeTab === 'insights' && (
          <Card className="bg-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-display text-obsidian-night flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-burnt-terra" />
                    User Insights
                  </CardTitle>
                  <CardDescription className="font-body text-shadow-pine/60">
                    Detailed streaks, voice analysis data, and practice progress per user
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchDetailedUserData}
                  disabled={loadingDetailed}
                >
                  {loadingDetailed ? 'Loading...' : 'Refresh'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingDetailed ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burnt-terra"></div>
                </div>
              ) : detailedUserData.length === 0 ? (
                <div className="text-center py-8 text-shadow-pine/60">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No user data yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 p-4 bg-burnt-terra/10 rounded-lg">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-burnt-terra">
                        {detailedUserData.length}
                      </p>
                      <p className="text-xs text-burnt-terra">Total Users</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-shadow-pine">
                        {detailedUserData.filter(u => u.streaks.listening > 0 || u.streaks.speaking > 0).length}
                      </p>
                      <p className="text-xs text-shadow-pine">Active Streaks</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-burnt-terra">
                        {detailedUserData.reduce((sum, u) => sum + u.total_voice_analyses, 0)}
                      </p>
                      <p className="text-xs text-burnt-terra/70">Voice Analyses</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-burnt-terra">
                        {detailedUserData.reduce((sum, u) => sum + u.practice_progress.listening.completed + u.practice_progress.speaking.completed, 0)}
                      </p>
                      <p className="text-xs text-burnt-terra">Lessons Done</p>
                    </div>
                  </div>

                  {/* User List with Expandable Details */}
                  <div className="divide-y divide-shadow-pine/10">
                    {detailedUserData.map((userData) => (
                      <div key={userData.user_id} className="py-4">
                        {/* User Header - Clickable */}
                        <div 
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 cursor-pointer hover:bg-ivory-mist/50 -mx-4 px-4 py-2 rounded-lg transition-colors"
                          onClick={() => setExpandedUser(expandedUser === userData.user_id ? null : userData.user_id)}
                        >
                          {/* User Info */}
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-burnt-terra/20 rounded-full flex items-center justify-center flex-shrink-0">
                              <User className="w-5 h-5 text-burnt-terra" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-obsidian-night truncate">{userData.name}</p>
                              <p className="text-xs text-shadow-pine/60 truncate">{userData.email}</p>
                            </div>
                          </div>
                          
                          {/* Streak Badges */}
                          <div className="flex items-center gap-2 ml-13 sm:ml-0 flex-wrap">
                            {userData.streaks.listening > 0 && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-shadow-pine/20 rounded-full">
                                <Headphones className="w-3 h-3 text-shadow-pine" />
                                <Flame className="w-3 h-3 text-burnt-terra" />
                                <span className="text-xs font-bold text-shadow-pine">{userData.streaks.listening}</span>
                              </div>
                            )}
                            {userData.streaks.speaking > 0 && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-burnt-terra/10 rounded-full">
                                <Mic className="w-3 h-3 text-burnt-terra" />
                                <Flame className="w-3 h-3 text-burnt-terra" />
                                <span className="text-xs font-bold text-burnt-terra">{userData.streaks.speaking}</span>
                              </div>
                            )}
                            {userData.total_voice_analyses > 0 && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-burnt-terra/20 rounded-full">
                                <Activity className="w-3 h-3 text-burnt-terra" />
                                <span className="text-xs font-medium text-burnt-terra">{userData.total_voice_analyses} analyses</span>
                              </div>
                            )}
                            {expandedUser === userData.user_id ? (
                              <ChevronDown className="w-5 h-5 text-shadow-pine/40" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-shadow-pine/40" />
                            )}
                          </div>
                        </div>
                        
                        {/* Expanded Details */}
                        {expandedUser === userData.user_id && (
                          <div className="mt-4 ml-13 space-y-4 bg-ivory-mist/30 rounded-lg p-4">
                            {/* Practice Progress */}
                            <div>
                              <h4 className="text-sm font-semibold text-obsidian-night mb-2 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-burnt-terra" />
                                Practice Progress
                              </h4>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-shadow-pine/10 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Headphones className="w-4 h-4 text-shadow-pine" />
                                    <span className="text-sm font-medium text-obsidian-night">Listening</span>
                                  </div>
                                  <p className="text-lg font-bold text-shadow-pine">
                                    {userData.practice_progress.listening.completed}/{userData.practice_progress.listening.total}
                                  </p>
                                  <p className="text-xs text-shadow-pine">Cycle {userData.practice_progress.listening.current_cycle}</p>
                                </div>
                                <div className="bg-burnt-terra/10 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Mic className="w-4 h-4 text-burnt-terra" />
                                    <span className="text-sm font-medium text-burnt-terra">Speaking</span>
                                  </div>
                                  <p className="text-lg font-bold text-burnt-terra">
                                    {userData.practice_progress.speaking.completed}/{userData.practice_progress.speaking.total}
                                  </p>
                                  <p className="text-xs text-burnt-terra/70">Cycle {userData.practice_progress.speaking.current_cycle}</p>
                                </div>
                              </div>
                            </div>

                            {/* Assessments */}
                            {userData.assessments.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-obsidian-night mb-2 flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-shadow-pine" />
                                  Assessments Completed
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {userData.assessments.map((assessment, idx) => (
                                    <div key={idx} className="px-3 py-1.5 bg-ivory-mist rounded-full text-xs">
                                      <span className="font-medium text-obsidian-night capitalize">{assessment.style_type}</span>
                                      {assessment.archetype && (
                                        <span className="text-shadow-pine ml-1">({assessment.archetype})</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Voice Analysis History */}
                            {userData.voice_analyses.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-obsidian-night mb-2 flex items-center gap-2">
                                  <Activity className="w-4 h-4 text-burnt-terra" />
                                  Recent Voice Analysis ({userData.total_voice_analyses} total)
                                </h4>
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                  {userData.voice_analyses.map((analysis, idx) => (
                                    <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-3 border border-burnt-terra/20">
                                      <div className="flex items-center gap-3">
                                        <div className="text-center">
                                          <Calendar className="w-4 h-4 text-shadow-pine/50 mx-auto mb-1" />
                                          <p className="text-xs text-shadow-pine/60">
                                            {analysis.date ? new Date(analysis.date).toLocaleDateString() : 'N/A'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="font-semibold text-obsidian-night capitalize">
                                            {analysis.dominant_emotion}
                                          </p>
                                          <p className="text-xs text-shadow-pine/60">
                                            {analysis.emotion_confidence}% confidence
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex gap-2 text-xs">
                                        <span className="px-2 py-1 bg-ivory-mist text-shadow-pine rounded">
                                          Pitch: {analysis.pitch}
                                        </span>
                                        <span className="px-2 py-1 bg-shadow-pine/10 text-shadow-pine rounded">
                                          Pace: {analysis.pace}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* No Voice Analysis */}
                            {userData.voice_analyses.length === 0 && (
                              <div className="text-center py-4 text-shadow-pine/50">
                                <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No voice analysis data yet</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Whitelist Tab */}
        {activeTab === 'whitelist' && (
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="font-display text-obsidian-night">Access Whitelist</CardTitle>
              <CardDescription className="font-body text-shadow-pine/60">
                Control who can register for the app. Existing users maintain access. New users must be whitelisted.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Add new email form */}
              <form onSubmit={handleAddToWhitelist} className="mb-6 p-4 bg-shadow-pine/10 rounded-lg border border-shadow-pine/30">
                <h4 className="font-medium text-obsidian-night mb-3 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Add Email to Whitelist
                </h4>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={newWhitelistEmail}
                      onChange={(e) => setNewWhitelistEmail(e.target.value)}
                      className="flex-1 bg-white"
                      required
                    />
                    <Input
                      type="text"
                      placeholder="Full Name"
                      value={newWhitelistName}
                      onChange={(e) => setNewWhitelistName(e.target.value)}
                      className="flex-1 bg-white"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      type="text"
                      placeholder="Note (optional)"
                      value={newWhitelistNote}
                      onChange={(e) => setNewWhitelistNote(e.target.value)}
                      className="flex-1 bg-white"
                    />
                    <Button 
                      type="submit" 
                      disabled={addingToWhitelist}
                      className="bg-shadow-pine hover:bg-shadow-pine/90 text-white"
                    >
                      {addingToWhitelist ? 'Adding...' : 'Add to Whitelist'}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-shadow-pine mt-2">
                  Whitelisted users can login with their email. Account is auto-created with Marshall Jones as coach.
                </p>
              </form>

              {/* Bulk Import Section */}
              <div className="mb-6 p-4 bg-ivory-mist rounded-lg border border-shadow-pine/30">
                <h4 className="font-medium text-obsidian-night mb-3 flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4" />
                  Bulk Import from CSV
                </h4>
                <div className="flex flex-col sm:flex-row gap-3 items-start">
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleBulkImport}
                    ref={fileInputRef}
                    className="hidden"
                    id="csv-upload"
                  />
                  <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={bulkImporting}
                    variant="outline"
                    className="border-shadow-pine/40 text-shadow-pine hover:bg-shadow-pine/20"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {bulkImporting ? 'Importing...' : 'Upload CSV File'}
                  </Button>
                  <p className="text-xs text-shadow-pine">
                    CSV format: <code className="bg-shadow-pine/20 px-1 rounded">name,email</code> (one per line)
                  </p>
                </div>
                
                {/* Bulk Import Results */}
                {bulkImportResults && (
                  <div className="mt-4 space-y-2">
                    {bulkImportResults.success.length > 0 && (
                      <div className="flex items-center gap-2 text-shadow-pine">
                        <CheckCircle className="w-4 h-4" />
                        <span>{bulkImportResults.success.length} users added successfully</span>
                      </div>
                    )}
                    {bulkImportResults.skipped.length > 0 && (
                      <div className="text-burnt-terra">
                        <span className="font-medium">{bulkImportResults.skipped.length} skipped:</span>
                        <ul className="text-xs ml-6 mt-1">
                          {bulkImportResults.skipped.slice(0, 5).map((s, i) => (
                            <li key={i}>{s.data} - {s.reason}</li>
                          ))}
                          {bulkImportResults.skipped.length > 5 && (
                            <li>...and {bulkImportResults.skipped.length - 5} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                    {bulkImportResults.failed.length > 0 && (
                      <div className="flex items-center gap-2 text-red-700">
                        <XCircle className="w-4 h-4" />
                        <span>{bulkImportResults.failed.length} failed to import</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Whitelist entries */}
              {filteredWhitelist.length === 0 ? (
                <div className="text-center py-8 text-shadow-pine/60">
                  <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No whitelisted emails found</p>
                </div>
              ) : (
                <div className="divide-y divide-shadow-pine/10">
                  {filteredWhitelist.map((entry) => (
                    <div key={entry.id} className="py-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-shadow-pine/20 rounded-full flex items-center justify-center">
                          <Mail className="w-5 h-5 text-shadow-pine" />
                        </div>
                        <div className="ml-3">
                          <p className="font-body font-semibold text-obsidian-night">
                            {entry.name ? `${entry.name} (${entry.email})` : entry.email}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-shadow-pine/60">
                            {entry.note && <span className="bg-ivory-mist/80 px-2 py-0.5 rounded">{entry.note}</span>}
                            <span>Added {new Date(entry.created_at).toLocaleDateString()}</span>
                            {entry.added_by && <span>by {entry.added_by}</span>}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFromWhitelist(entry.id, entry.email)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Operation Listen Tab */}
        {activeTab === 'olg' && (
          <div className="space-y-4">
            {/* Big CTA: 4-player Master View */}
            <Card className="bg-gradient-to-br from-burnt-terra to-burnt-terra/80 text-white border-0">
              <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Headphones className="w-7 h-7" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-xl sm:text-2xl font-bold mb-1">Test the game with 4 players</h3>
                  <p className="text-white/85 text-sm">
                    Open a demo room with four dummy players — Coach, Guide, Player B, and Conflict Coach — side by side in one screen. Signal chains, escalate threat, watch every role's perspective update live.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/admin/operation-listen')}
                  className="bg-white text-burnt-terra hover:bg-white/90 font-semibold px-5 py-6 rounded-full whitespace-nowrap self-stretch sm:self-auto"
                  data-testid="admin-olg-master-cta"
                >
                  Open Master View
                </Button>
              </CardContent>
            </Card>

            {/* Active Sessions */}
            <Card className="bg-white">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-display text-obsidian-night flex items-center gap-2">
                      <Radio className="w-5 h-5 text-burnt-terra" />
                      Active Sessions ({(olgOverview.sessions || []).filter((s) => s.sessionStatus !== 'ended').length})
                    </CardTitle>
                    <CardDescription className="font-body text-shadow-pine/60">
                      Every facilitator-run session. Live lobby / group counts refresh on demand.
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchOlgOverview} disabled={loadingOlg} data-testid="admin-olg-refresh">
                    {loadingOlg ? 'Loading…' : 'Refresh'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {(olgOverview.sessions || []).length === 0 ? (
                  <div className="text-center py-8 text-shadow-pine/60">
                    <Radio className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No sessions have been created yet.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-shadow-pine/10">
                    {olgOverview.sessions.map((s) => (
                      <div key={s.code} className="py-3 flex items-center gap-3" data-testid={`admin-olg-session-${s.code}`}>
                        <div className="w-10 h-10 rounded-full bg-burnt-terra/15 flex items-center justify-center flex-shrink-0">
                          <span className="font-mono font-bold text-burnt-terra text-sm">{s.code}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-semibold text-obsidian-night">
                              {s.numGroups} groups · {s.gameMode}
                            </span>
                            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                              s.sessionStatus === 'active' ? 'bg-shadow-pine/15 text-shadow-pine' :
                              s.sessionStatus === 'lobby' ? 'bg-burnt-terra/15 text-burnt-terra' :
                              s.sessionStatus === 'debrief' ? 'bg-[#86AC93]/20 text-[#5D8574]' :
                              'bg-shadow-pine/10 text-shadow-pine/60'
                            }`}>{s.sessionStatus}</span>
                          </div>
                          <div className="text-xs text-shadow-pine/60 mt-0.5">
                            {s.lobbyCount} in lobby · {(s.groups || []).length} rooms spawned · {s.nudgeCount} nudge{s.nudgeCount === 1 ? '' : 's'}
                          </div>
                        </div>
                        {s.sessionStatus !== 'ended' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => endOlgSession(s.code)}
                            className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                            data-testid={`admin-olg-end-session-${s.code}`}
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Active Rooms */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="font-display text-obsidian-night flex items-center gap-2">
                  <Users className="w-5 h-5 text-shadow-pine" />
                  Active Rooms ({(olgOverview.rooms || []).length})
                </CardTitle>
                <CardDescription className="font-body text-shadow-pine/60">
                  Every room in play — standalone rooms, session-spawned rooms, and admin demo rooms.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(olgOverview.rooms || []).length === 0 ? (
                  <div className="text-center py-8 text-shadow-pine/60">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No rooms are currently live.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-shadow-pine/10">
                    {olgOverview.rooms.map((r) => {
                      const st = r.state || {};
                      return (
                        <div key={r.code} className="py-3" data-testid={`admin-olg-room-${r.code}`}>
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-shadow-pine/15 flex items-center justify-center flex-shrink-0">
                              <span className="font-mono font-bold text-shadow-pine text-sm">{r.code}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <span className="font-semibold text-obsidian-night">
                                  {r.playerCount}/4 players
                                </span>
                                {r.demo && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-burnt-terra/15 text-burnt-terra">demo</span>}
                                {r.sessionCode && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-shadow-pine/15 text-shadow-pine">session {r.sessionCode} · G{r.groupNum}</span>}
                                <span className="text-[10px] uppercase tracking-wider text-shadow-pine/50">phase: {st.phase || 'lobby'}</span>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {(r.players || []).map((p, i) => (
                                  <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${p.dummy ? 'bg-burnt-terra/10 text-burnt-terra' : 'bg-shadow-pine/10 text-shadow-pine'}`}>
                                    {p.name} · {p.role}
                                  </span>
                                ))}
                              </div>
                              {st.signaledChain && (
                                <div className="text-xs text-burnt-terra mt-1">
                                  Signal: <span className="font-mono">{st.signaledChain}</span> · Threat {st.threatLevel || 0}/4
                                </div>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => endOlgRoom(r.code)}
                              className="text-red-500 border-red-500/30 hover:bg-red-500/10 flex-shrink-0"
                              data-testid={`admin-olg-end-room-${r.code}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* User Experience Tab - Admin Access to App Features */}
        {activeTab === 'app' && (
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="font-display text-obsidian-night">User Experience</CardTitle>
              <CardDescription className="font-body text-shadow-pine/60">
                Jump into any hub as an admin to test and explore the current app.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* The Body */}
                <div
                  onClick={() => navigate('/client/flow-grid')}
                  className="p-6 bg-gradient-to-br from-burnt-terra/10 to-burnt-terra/5 rounded-xl border border-burnt-terra/20 cursor-pointer hover:shadow-lg transition-all group"
                  data-testid="admin-the-body"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-burnt-terra/20 rounded-full flex items-center justify-center group-hover:bg-burnt-terra/30 transition-colors">
                      <Dumbbell className="w-6 h-6 text-burnt-terra" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-obsidian-night">The Body</h3>
                      <p className="text-xs text-shadow-pine/60">Physical workout · Breathwork · Reset · Drills</p>
                    </div>
                  </div>
                  <p className="text-sm text-shadow-pine/80">
                    FlowGrid workout with tool selection, Control Pause breathwork, and the reset module.
                  </p>
                </div>

                {/* The Voice */}
                <div
                  onClick={() => navigate('/client/voice')}
                  className="p-6 bg-gradient-to-br from-burnt-terra/15 to-burnt-terra/5 rounded-xl border border-burnt-terra/20 cursor-pointer hover:shadow-lg transition-all group"
                  data-testid="admin-the-voice"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-burnt-terra/20 rounded-full flex items-center justify-center group-hover:bg-burnt-terra/30 transition-colors">
                      <Mic className="w-6 h-6 text-burnt-terra" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-obsidian-night">The Voice</h3>
                      <p className="text-xs text-shadow-pine/60">Tonal Influence · Listening Chain · AAA · Drills · OL Game</p>
                    </div>
                  </div>
                  <p className="text-sm text-shadow-pine/80">
                    Speaking practice, Listening Chain framework, AAA checklist, voice drills, and the Operation Listen multiplayer game.
                  </p>
                </div>

                {/* The Story */}
                <div
                  onClick={() => navigate('/client/excavation')}
                  className="p-6 bg-gradient-to-br from-shadow-pine/15 to-shadow-pine/5 rounded-xl border border-shadow-pine/25 cursor-pointer hover:shadow-lg transition-all group"
                  data-testid="admin-the-story"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-shadow-pine/25 rounded-full flex items-center justify-center group-hover:bg-shadow-pine/35 transition-colors">
                      <PenLine className="w-6 h-6 text-shadow-pine" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-obsidian-night">The Story</h3>
                      <p className="text-xs text-shadow-pine/60">The Story We Tell · 90-day excavation</p>
                    </div>
                  </div>
                  <p className="text-sm text-shadow-pine/80">
                    Reflect-then-write flow. In-app 2-minute reflection timer, physical journal for writing.
                  </p>
                </div>

                {/* Operation Listen (Master View) */}
                <div
                  onClick={() => navigate('/admin/operation-listen')}
                  className="p-6 bg-gradient-to-br from-burnt-terra to-burnt-terra/70 rounded-xl border-0 cursor-pointer hover:shadow-lg transition-all group text-white"
                  data-testid="admin-olg-master"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                      <Radio className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold">Operation Listen — Master View</h3>
                      <p className="text-xs text-white/80">4-player demo · all roles side by side</p>
                    </div>
                  </div>
                  <p className="text-sm text-white/85">
                    Spin up a demo room with four dummy players and watch Coach, Guide, Player B, and Conflict Coach perspectives update in real time.
                  </p>
                </div>

                {/* Client Hub */}
                <div
                  onClick={() => navigate('/client/hub')}
                  className="p-6 bg-gradient-to-br from-ivory-mist to-white rounded-xl border border-shadow-pine/20 cursor-pointer hover:shadow-lg transition-all group"
                  data-testid="admin-client-hub"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-shadow-pine/15 rounded-full flex items-center justify-center group-hover:bg-shadow-pine/25 transition-colors">
                      <Users className="w-6 h-6 text-shadow-pine" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-obsidian-night">Main Hub</h3>
                      <p className="text-xs text-shadow-pine/60">Three-hub landing screen</p>
                    </div>
                  </div>
                  <p className="text-sm text-shadow-pine/80">
                    See exactly what clients see when they open the app — The Body / The Voice / The Story landing.
                  </p>
                </div>

                {/* Breathwork */}
                <div
                  onClick={() => navigate('/client/hub')}
                  className="p-6 bg-gradient-to-br from-shadow-pine/10 to-white rounded-xl border border-shadow-pine/20 cursor-pointer hover:shadow-lg transition-all group"
                  data-testid="admin-breathwork"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-shadow-pine/15 rounded-full flex items-center justify-center group-hover:bg-shadow-pine/25 transition-colors">
                      <Wind className="w-6 h-6 text-shadow-pine" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-obsidian-night">Control Pause</h3>
                      <p className="text-xs text-shadow-pine/60">Breathwork with auto-pause on lock</p>
                    </div>
                  </div>
                  <p className="text-sm text-shadow-pine/80">
                    Nasal breathing + breath-hold timer. Freezes on phone-lock / call interruptions.
                  </p>
                </div>
              </div>

              {/* Note */}
              <div className="mt-6 p-4 bg-ivory-mist rounded-lg border border-shadow-pine/30">
                <p className="text-sm text-obsidian-night">
                  <strong>Note:</strong> Admin access to all hubs. Your progress is tracked separately from regular users.
                  For the Operation Listen game itself, jump to <span className="font-mono text-burnt-terra">Operation Listen</span> tab or the Master View to test with 4 dummy players.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      <BrandMark />
    </div>
  );
};

export default AdminDashboard;
